import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ShieldCheck, Sparkles, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";
import { AFFILIATE_URLS, onAffiliateClick } from "../config/affiliate";

const BASE = "https://www.peptidesformedspas.org";

const PARTNERS = [
  {
    name: "AlphaSync Rx",
    domain: "www.alphasyncrx.com",
    url: AFFILIATE_URLS.alphasync,
    partnerKey: "alphasync",
    tagline: "Modern dispensing infrastructure for physicians and licensed pharmacy partners",
    description:
      "AlphaSync Rx is a physician-facing dispensing-infrastructure platform that connects licensed clinicians with licensed pharmacy partners. It enables physicians to coordinate product dispensing (peptides, hormone therapies, fertility medications, pharmacy-grade nutraceuticals, at-home diagnostics, and longevity-focused therapies) directly to their patients — without the clinician managing inventory, fulfillment, or shipping logistics.",
    builtFor: [
      "Longevity & performance medicine practices",
      "Hormone optimization clinics",
      "Telehealth platforms",
      "Functional & integrative medicine clinics",
      "Concierge & membership-based care",
    ],
    complianceNotes: [
      "Physician-centered: clinicians retain full control over medical necessity and treatment plans",
      "Pharmacy-fulfillment separation — orders are filled by licensed pharmacy partners, not by AlphaSync or by us",
      "No prescribing requirements or exclusivity",
      "Designed with state-by-state regulatory adaptability",
    ],
    contact: { email: "info@alphasyncrx.com", phone: "+1 (888) 919-5097" },
  },
  {
    name: "MyDose.ai",
    domain: "www.mydose.ai",
    url: AFFILIATE_URLS.mydose,
    partnerKey: "mydose",
    tagline: "Peptide-therapy management platform for licensed clinicians",
    description:
      "MyDose.ai is a peptide-therapy management platform for licensed med spas, longevity clinics, gyms, and telehealth providers. It handles patient intake, clinician oversight, protocol tracking, and pharmacy fulfillment workflows — designed for practices managing dozens or hundreds of peptide-related patients across one or multiple locations.",
    builtFor: [
      "Med spas operating under licensed medical direction",
      "Longevity & wellness clinics",
      "Gyms with clinician-supervised wellness programs",
      "Telehealth providers running peptide-related care models",
    ],
    complianceNotes: [
      "Operated for licensed clinicians and clinical practices — not for direct-to-consumer sale",
      "All prescribing, dosing, and clinical decisions remain with the licensed clinician using the platform",
      "Pharmacy fulfillment routed through licensed pharmacies",
    ],
    contact: { url: "https://mydose.ai/" },
  },
  {
    name: "Alohi Skin & Wellness",
    domain: "www.alohiskinandwellness.com",
    url: AFFILIATE_URLS.alohi,
    partnerKey: "alohi",
    tagline: "Some incredible skin care we work with — the GHK Facemask is one of the best we have seen",
    description:
      "Alohi Skin & Wellness is a skincare brand partner we recommend for topical formulations that pair with our peptide-education work. Their GHK Facemask is one of the strongest GHK-Cu-forward at-home cosmetic products we have evaluated. Alohi is a retail cosmetics brand — not a pharmacy, medical provider, or peptide compounder — and every product they sell is for cosmetic use.",
    builtFor: [
      "Consumers interested in cosmetic-grade GHK-Cu formulations",
      "Med spas curating an at-home skincare complement to in-office treatments",
      "Anyone researching topical GHK-Cu after reading our GHK-Cu peptide page",
    ],
    complianceNotes: [
      "Alohi products are retail cosmetics — not drugs, not compounded prescriptions, not research chemicals",
      "This is a partner recommendation; PeptidesForMedSpas does not sell or ship Alohi products",
      "Cosmetic-only positioning; no therapeutic claims",
    ],
    contact: { url: "https://www.alohiskinandwellness.com/" },
  },
];

const PartnersPage = () => {
  usePageSEO();

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${BASE}/partners#webpage`,
      url: `${BASE}/partners`,
      name: "Partners | PeptidesForMedSpas",
      description:
        "Educational referral partners for licensed clinicians and pharmacy fulfillment: AlphaSync Rx (dispensing infrastructure) and MyDose.ai (peptide-therapy management platform). PeptidesForMedSpas is an educational storefront — not a pharmacy or medical provider.",
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      breadcrumb: { "@id": `${BASE}/partners#breadcrumb` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${BASE}/partners#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Partners", item: `${BASE}/partners` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${BASE}/partners#itemlist`,
      name: "Educational Referral Partners",
      itemListElement: PARTNERS.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Organization",
          name: p.name,
          url: p.url,
          description: p.description,
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="partners-page">
      <Header />
      <JsonLd id="partners-jsonld" data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-block px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <span className="text-sm text-[#b89a6d] font-medium">Educational Referral Partners</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="partners-h1">
            Partners
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            PeptidesForMedSpas works closely with{" "}
            <strong className="text-[#3a2a26]">AlphaSync Rx</strong> and{" "}
            <strong className="text-[#3a2a26]">MyDose.ai</strong> as educational referral partners.
            Readers who want clinician-supervised pathways for peptide-related care can learn about
            both platforms here. We are not a pharmacy, a med spa, or a medical provider — and these
            partnerships do not change that.
          </p>
        </div>
      </section>

      {/* What this is / What this isn't */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-[#f0d8d4] rounded-2xl p-7" data-testid="partners-what-this-is">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className="w-6 h-6 text-[#b89a6d]" />
              <h2 className="text-lg font-bold text-[#3a2a26]">What this is</h2>
            </div>
            <ul className="space-y-2 text-sm md:text-base text-[#5a4a40] leading-relaxed">
              <li>An educational pointer to two platforms used by licensed clinicians</li>
              <li>A way for readers who want clinician-supervised options to find them</li>
              <li>Compliance with our own framing: education and transparency first</li>
            </ul>
          </div>
          <div className="bg-white border border-[#f0d8d4] rounded-2xl p-7" data-testid="partners-what-this-isnt">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="w-6 h-6 text-[#b89a6d]" />
              <h2 className="text-lg font-bold text-[#3a2a26]">What this is not</h2>
            </div>
            <ul className="space-y-2 text-sm md:text-base text-[#5a4a40] leading-relaxed">
              <li>Not medical advice, not a referral to a specific clinician</li>
              <li>Not a sales channel — PeptidesForMedSpas does not prescribe, dispense, or fulfill medications</li>
              <li>Not a guarantee of outcomes from any partner platform or clinician</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Partner cards */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {PARTNERS.map((p, idx) => (
            <article
              key={p.name}
              className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-8 md:p-10"
              data-testid={`partner-card-${idx}`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-[#b89a6d]" />
                    <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26]">{p.name}</h2>
                  </div>
                  <p className="text-sm text-[#7a6b62]">{p.tagline}</p>
                </div>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  data-testid={`partner-link-${idx}`}
                  onClick={onAffiliateClick({ partner: p.partnerKey, product: "partners-page-card", url: p.url })}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#d4a5ad] hover:bg-[#c4949c] text-white font-semibold rounded-lg transition-colors text-sm"
                >
                  Visit {p.domain}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed mb-6">{p.description}</p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-bold text-[#3a2a26] mb-3 uppercase tracking-wide">
                    Built for
                  </h3>
                  <ul className="space-y-1.5 text-sm text-[#5a4a40]">
                    {p.builtFor.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <ArrowRight className="w-4 h-4 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#3a2a26] mb-3 uppercase tracking-wide">
                    Compliance posture
                  </h3>
                  <ul className="space-y-1.5 text-sm text-[#5a4a40]">
                    {p.complianceNotes.map((c) => (
                      <li key={c} className="flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Compliance disclosure */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6">
            <h2 className="text-base font-bold text-[#3a2a26] mb-3">
              Disclosure — Educational referral relationship
            </h2>
            <p className="text-sm text-[#5a4a40] leading-relaxed mb-3">
              The relationship between PeptidesForMedSpas and the partners listed on this page is an
              <strong> educational referral relationship.</strong> We do not prescribe, compound, or dispense
              medications. We do not collect protected health information. We do not bill insurance.
              We do not direct readers to specific clinicians or treatment plans. Readers who want clinician-supervised
              peptide-related care can learn about these partners — and decide for themselves whether
              to engage with a licensed clinician operating on those platforms.
            </p>
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              For our complete compliance posture across federal and state law, see our{" "}
              <Link to="/compliance" className="text-[#b89a6d] underline hover:text-[#a08560]" data-testid="partners-to-compliance">
                Compliance & Regulatory Page
              </Link>.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnersPage;
